import dayjs from 'dayjs';
import React, { useState } from 'react';
import ConfirmModal from '../../common/ConfirmModal';
import PillBox from '../../common/form/PillBox';
import List from '../../common/list/List';
import ListItem from '../../common/list/ListItem';
import { OpenClosedStates } from '../../common/OpenClosedStates';
import { upperCaseFirstLetter } from '../../helpers';
import useDisplayAdRequests from './hooks/useDisplayAdRequests';
import DisplayAdDetailsModal from './DisplayAdDetailsModal';
import { DisplayAdForm } from './DisplayAdDetailsModal';

const DisplayAdRequestsList = () => {
	const {
		isFetching,
		data,
		hasNextPage,
		isFetchingNextPage,
		invoice,
		cancel,
		update,
		fetchNextPage,
	} = useDisplayAdRequests();
	const [confirmInvoiceState, setConfirmInvoiceState] =
		useState<OpenClosedStates>(OpenClosedStates.Closed);
	const [confirmCancelState, setConfirmCancelState] =
		useState<OpenClosedStates>(OpenClosedStates.Closed);
	const [isInvoicing, setIsInvoicing] = useState(false);
	const [isCanceling, setIsCanceling] = useState(false);
	const [detailsModalState, setDetailsModalState] =
		useState<OpenClosedStates>(OpenClosedStates.Closed);
	const [currId, setCurrId] = useState<number>();
	const [currAd, setCurrAd] = useState<Partial<DisplayAdForm>>();

	const invoiceRequest = async () => {
		if (isInvoicing) return;
		setIsInvoicing(true);
		try {
			await invoice(currId);
			setConfirmInvoiceState(OpenClosedStates.Closed);
		} finally {
			setIsInvoicing(false);
		}
	};
	const cancelRequest = async () => {
		if (isCanceling) return;
		setIsCanceling(true);
		try {
			await cancel(currId);
			setConfirmCancelState(OpenClosedStates.Closed);
		} finally {
			setIsCanceling(false);
		}
	};
	const handleUpdate = async (updatedAd: Partial<DisplayAdForm>) => {
		await update(updatedAd);
		setDetailsModalState(OpenClosedStates.Closed);
	};

	return (
		<>
			<List
				isFetching={isFetching}
				isFetchingNextPage={!!isFetchingNextPage}
				fetchNextPage={fetchNextPage}
				hasNextPage={hasNextPage}
			>
				{data?.pages?.map((page: any, pageI: number) => (
					<React.Fragment key={pageI}>
						{page?.data?.map((row: any, rowI: number) => (
							<div
								key={rowI}
								className="w-full cursor-pointer"
								onClick={() => {
									setCurrAd(row);
									setDetailsModalState(OpenClosedStates.Open);
								}}
							>
								<ListItem index={rowI}>
									<div className="flex flex-row items-center justify-between space-x-4 w-full">
										<div className="min-w-0">
											<div className="relative group flex items-center">
												<div className="flex items-center flex-shrink w-full">
													{(row.urlSmall || row.urlLarge) ? (
														<div className="flex gap-2 mr-2">
															{row.urlSmall ? (
																<div className="flex flex-col gap-1 items-center">
																	<span className="text-xs text-gray-500">Small</span>
																	<img
																		src={row.urlSmall}
																		alt="Small"
																		className="w-16 h-16 object-contain"
																	/>
																</div>
															) : null}
															{row.urlLarge ? (
																<div className="flex flex-col gap-1 items-center">
																	<span className="text-xs text-gray-500">Large</span>
																	<img
																		src={row.urlLarge}
																		alt="Large"
																		className="w-16 h-16 object-contain"
																	/>
																</div>
															) : null}
														</div>
													) : null}
													<div className="flex-1 flex flex-col gap-1 px-4 w-40 text-sm h-8 mb-2 md:mb-0">
														<label className="font-bold cursor-pointer">Requestor</label>
														<h2 className="text-xs font-medium leading-4 w-full text-clip">
															{row.firstName} {row.lastName} — {row.company}
														</h2>
													</div>
													<div className="flex-1 flex-col gap-1 px-4 w-40 text-sm h-8 hidden md:block">
														<label className="font-bold cursor-pointer">Views</label>
														<h2 className="text-xs font-medium leading-4">
															{row.views || 0}
														</h2>
													</div>
													<div className="flex-1 flex-col gap-1 px-4 w-40 text-sm h-8 hidden md:block">
														<label className="font-bold cursor-pointer">Clicks</label>
														<h2 className="text-xs font-medium leading-4">
															{row.clicks || 0}
														</h2>
													</div>
												</div>
											</div>
										</div>
										<div>
											<div className="flex flex-row overflow-x-auto flex-wrap gap-2 w-40">
												<PillBox
													className={
														row.cancelAtPeriodEnd
															? 'bg-red-600 cursor-not-allowed focus:ring-red-500 shadow-sm'
															: row.subscriptionStatus === 'active'
															? 'bg-primary-600 cursor-not-allowed focus:ring-primary-500 shadow-sm'
															: row.subscriptionStatus === 'past_due'
															? 'bg-red-600 cursor-not-allowed focus:ring-red-500 shadow-sm'
															: row.subscriptionStatus === 'canceled'
															? 'bg-gray-600 cursor-not-allowed focus:ring-gray-500 shadow-sm'
															: row.approved
															? 'bg-yellow-600 cursor-not-allowed focus:ring-yellow-500 shadow-sm'
															: 'bg-gray-600 cursor-not-allowed focus:ring-gray-500 shadow-sm'
													}
													text={
														row.cancelAtPeriodEnd
															? 'Cancellation Pending'
															: row.subscriptionStatus === 'active'
															? 'Active'
															: row.subscriptionStatus === 'past_due'
															? 'Payment Failed'
															: row.subscriptionStatus === 'canceled'
															? 'Canceled'
															: row.approved
															? 'Approved - Awaiting Payment'
															: 'Pending Approval'
													}
													onClick={() => null}
													loading={false}
												/>
												{!row.invoicePaid && !row.invoiced ? (
													<div onClick={(e) => e.stopPropagation()}>
														<PillBox
															className="bg-gray-600 hover:bg-gray-700 focus:ring-gray-500 shadow-sm cursor-pointer"
															text="Invoice this request"
															onClick={() => {
																setCurrId(row.id);
																setConfirmInvoiceState(OpenClosedStates.Open);
															}}
															loading={false}
														/>
													</div>
												) : null}
												{row.invoiced ? (
													<PillBox
														className={
															row.invoicePaid
																? 'bg-primary-600 cursor-not-allowed focus:ring-primary-500 shadow-sm'
																: 'bg-gray-600 cursor-not-allowed focus:ring-gray-500 shadow-sm'
														}
														text={
															row.invoicePaid
																? 'Invoice paid'
																: `Invoiced ${dayjs(row.invoicedDate).format('MM/DD/YYYY')}`
														}
														onClick={() => null}
														loading={false}
														disabled
													/>
												) : null}
												{row.subscriptionStatus === 'active' && !row.cancelAtPeriodEnd ? (
													<div onClick={(e) => e.stopPropagation()}>
														<PillBox
															className="bg-red-600 hover:bg-red-700 focus:ring-red-500 shadow-sm cursor-pointer"
															text="Cancel Subscription"
															onClick={() => {
																setCurrId(row.id);
																setConfirmCancelState(OpenClosedStates.Open);
															}}
															loading={false}
														/>
													</div>
												) : null}
											</div>
										</div>
									</div>
								</ListItem>
							</div>
						))}
					</React.Fragment>
				))}
			</List>

			<ConfirmModal
				title="Invoice this request?"
				description="This will send a Stripe subscription signup link to the requestor's email. Once they subscribe, the display ad will go live automatically."
				state={confirmInvoiceState}
				confirm={invoiceRequest}
				close={() => setConfirmInvoiceState(OpenClosedStates.Closed)}
				loading={isInvoicing}
			/>
			<ConfirmModal
				title="Cancel subscription?"
				description="This will cancel the display ad subscription at the end of the current billing period. The ad will remain active until then."
				state={confirmCancelState}
				confirm={cancelRequest}
				close={() => setConfirmCancelState(OpenClosedStates.Closed)}
				loading={isCanceling}
			/>
			{currAd ? (
				<DisplayAdDetailsModal
					state={detailsModalState}
					request={currAd}
					close={() => {
						setCurrAd(undefined);
						setDetailsModalState(OpenClosedStates.Closed);
					}}
					onUpdate={handleUpdate}
				/>
			) : null}
		</>
	);
};

export default DisplayAdRequestsList;
